import React, { Fragment, useCallback, useContext, useState } from 'react'
import { Typography } from '@mui/material'
import tw from 'twin.macro'
import { ReloadWrapperContext } from 'src/components/wrapper/reload-wrapper/ReloadWrapper.context'
import { useDispatch, useSelector } from 'react-redux'
import { Tag } from 'src/interfaces/tag'
import { getMarkets, getSelectedMarket } from 'src/selectors/market'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { Button } from 'src/components/shared/Button'
import { Tags } from 'src/components/tags/Tags'
import { handleApiError } from 'src/utils/errorHandlers'
import { showToast } from 'src/utils/toast'
import { Formik } from 'formik'
import { colors } from 'src/theme'
import { GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { ApproveSubcontractorRequestsSchema } from './ApproveSubcontractorRequestsForm.validator'
import { ApproveSubcontractorRequestsFormValues } from './ApproveSubcontractorRequestsForm.interface'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { ApproveSubcontractorRequests } from 'src/constants/actionTypes'
import { addS } from 'src/utils/baseUtils'

interface Props {
  subcontractorRequestsSelection?: string[] | GridRowSelectionModel
  onClose(): void
}

export const ApproveSubcontractorRequestsForm = ({
  onClose,
  subcontractorRequestsSelection,
}: Props) => {
  const dispatch = useDispatch()

  const selectedMarket = useSelector(getSelectedMarket)
  const markets = useSelector(getMarkets)

  const { reload } = useContext(ReloadWrapperContext)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [selectedTags, setSelectedTags] = useState<Array<Tag>>([])

  const handleFormSubmit = async (values: ApproveSubcontractorRequestsFormValues) => {
    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        ApproveSubcontractorRequests.request({
          selectedSubcontractorRequestIds: subcontractorRequestsSelection,
          tags: selectedTags.map((item) => item.name),
          partnerMarketId: values.partnerMarketId,
        }),
      )

      showToast(
        `${addS(
          subcontractorRequestsSelection ? subcontractorRequestsSelection.length : 0,
          'Contractor',
        )} approved. You can now manage ${
          subcontractorRequestsSelection && subcontractorRequestsSelection.length === 1
            ? 'it'
            : 'them'
        } in the 'Contractors' tab.`,
      )

      reload()
      onClose()
    } catch (err: any) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelectTag = useCallback((newTag: Tag) => {
    setSelectedTags((prev) => [...prev, newTag])
  }, [])

  const handleRemoveTag = useCallback((removedTag: Tag) => {
    setSelectedTags((prev) => [...prev.filter((item) => item.name !== removedTag.name)])
  }, [])

  return (
    <Formik
      initialValues={{
        partnerMarketId: selectedMarket?.id,
      }}
      onSubmit={handleFormSubmit}
      validationSchema={ApproveSubcontractorRequestsSchema}
    >
      {({ handleSubmit }) => (
        <Fragment>
          <div data-testid="requests-main-approve-subcontractor-form" css={tw`px-4 pb-8`}>
            <Typography
              variant="body2"
              color={colors.SHADES_GREY_400}
              css={tw`py-4 border-0 border-b border-solid border-[#EDEDED] mb-4`}
            >
              Once approved, the contractor will appear in the 'Contractors' tab.
            </Typography>
            <FormikSelect
              name="partnerMarketId"
              label="Market"
              placeholder="Select market"
              options={markets.map((item) => ({
                label: item.title,
                value: item.id,
              }))}
              inputProps={{
                'data-testid': 'requests-main-approve-subcontractor-market-select-input',
              }}
            />
            <Tags
              tags={selectedTags}
              className={tw`mt-4`}
              onSelectTag={handleSelectTag}
              onRemoveTag={handleRemoveTag}
              addTagTriggerDataTestId="requests-main-approve-subcontractor-add-tag-btn"
              tagMenuItemTestIdPrefix="requests-main-approve-subcontractor-tag"
            />
          </div>
          <div
            css={tw`flex flex-col justify-end items-end border-0 border-t border-solid border-[#EDEDED] p-4 pb-3 pt-3`}
          >
            <Button
              variant="contained"
              size="small"
              loading={isLoading}
              onClick={() => handleSubmit()}
              data-testid="requests-main-approve-subcontractor-submit-btn"
            >
              Approve
            </Button>
          </div>
        </Fragment>
      )}
    </Formik>
  )
}
